const SEPARATOR = ",";

function removeBlankEdgeLines(lines: string[]): string[] {
  let start = 0;
  let end = lines.length;
  while (start < end && lines[start].trim() === "") start++;
  while (end > start && lines[end - 1].trim() === "") end--;
  return lines.slice(start, end);
}

/** A suggestion line may end with the comma that separates it from the next item. */
function isSuggestionLine(line: string, target: string): boolean {
  const text = line.trim();
  return text === target || text === `${target}${SEPARATOR}`;
}

/** True when the prompt contains a line that is exactly this suggestion. */
export function isSuggestionInPrompt(prompt: string, suggestion: string): boolean {
  const target = suggestion.trim();
  if (!target) return false;
  return prompt.split("\n").some((line) => isSuggestionLine(line, target));
}

/** Adds the suggestion on a new line, comma-separated from the text before it. */
export function appendSuggestionToPrompt(prompt: string, suggestion: string): string {
  const target = suggestion.trim();
  if (!target || isSuggestionInPrompt(prompt, target)) return prompt;
  const base = prompt.replace(/\s+$/, "");
  if (!base) return target;
  const separated = base.endsWith(SEPARATOR) ? base : `${base}${SEPARATOR}`;
  return `${separated}\n${target}`;
}

/** Removes only the lines that match this suggestion; all other text is kept. */
export function removeSuggestionFromPrompt(prompt: string, suggestion: string): string {
  const target = suggestion.trim();
  const lines = prompt.split("\n");
  const kept = lines.filter((line) => !isSuggestionLine(line, target));
  if (kept.length === lines.length) return prompt;

  const contentLines = removeBlankEdgeLines(lines);
  const originalLast = contentLines[contentLines.length - 1] ?? "";
  const remaining = removeBlankEdgeLines(kept);
  if (remaining.length > 0 && isSuggestionLine(originalLast, target)) {
    const last = remaining.length - 1;
    remaining[last] = remaining[last].replace(/,\s*$/, "");
  }
  return remaining.join("\n");
}

export function toggleSuggestionInPrompt(prompt: string, suggestion: string): string {
  return isSuggestionInPrompt(prompt, suggestion)
    ? removeSuggestionFromPrompt(prompt, suggestion)
    : appendSuggestionToPrompt(prompt, suggestion);
}

/**
 * Chip bindings for a prompt. Selection is read from the prompt text itself, so
 * highlighted chips always match what is in the input.
 */
export function bindSuggestionChips(prompt: string, applyPrompt: (next: string) => void) {
  return {
    isSelected: (suggestion: string) => isSuggestionInPrompt(prompt, suggestion),
    onSelect: (suggestion: string) => applyPrompt(toggleSuggestionInPrompt(prompt, suggestion)),
  };
}
